import React, { useState, useRef, useEffect, useCallback } from "react";
import io from "socket.io-client";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import SherComboBox from "../../ComboBoxes/SherComboBox";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import axios from "axios";
import { useDbContext } from "../../../context/DbContext";
import { useSelector } from "react-redux";
import { selectActiveLocation } from "../../../app/locationSlice";
import { selectMenuPermissions } from "../../../app/permissionsSlice";
import { 
  FiSearch, FiFilter, FiDownload, FiPlus, FiTrash2, FiEdit3, 
  FiEye, FiEyeOff, FiUser, FiMail, FiPhone, FiLock, FiBookOpen,
  FiCreditCard, FiCheckCircle, FiInfo, FiChevronLeft, FiChevronRight,
  FiChevronsLeft, FiChevronsRight, FiMoreVertical
} from "react-icons/fi";
import { FaUserPlus, FaUsers, FaUserCheck, FaUserTimes, FaListAlt } from "react-icons/fa";

function CustomerAppReg({ onClose, zIndex, onOpenSchemeRegistration }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const activeLocation = useSelector(selectActiveLocation);
  const permissions = useSelector(selectMenuPermissions);

  const formPermissions = permissions?.["customerAppReg"]?.actions || {};
  const hasSavePermission = formPermissions.Save ?? true;
  const hasEditPermission = formPermissions.Edit ?? true;
  const hasDeletePermission = formPermissions.Delete ?? true;
  const hasFindPermission = formPermissions.Find ?? true;

  // Dynamic Options States
  const [schemeOptions, setSchemeOptions] = useState([]);
  const [customerNameOptions, setCustomerNameOptions] = useState([]);
  const [accountNoOptions, setAccountNoOptions] = useState([]);

  // Component States
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("All"); // "All" | "Active" | "Inactive"
  const [showPassword, setShowPassword] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    schemeName: { value: "", label: "" },
    customerName: { value: "", label: "" },
    accountNo: { value: "", label: "" },
    userName: "",
    password: "",
    email: "",
    mobile: "",
    status: "Active"
  });

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Refs for inputs and buttons
  const schemeComboRef = useRef(null);
  const customerComboRef = useRef(null);
  const accountComboRef = useRef(null);
  const userNameRef = useRef(null);
  const passwordRef = useRef(null);
  const emailRef = useRef(null);
  const mobileRef = useRef(null);
  const statusToggleRef = useRef(null);
  const saveButtonRef = useRef(null);

  // Focus next input on Enter key press
  const handleKeyDownNext = (e, nextRef) => {
    if (e.key === "Enter") {
      e.preventDefault();
      nextRef?.current?.focus();
      if (nextRef?.current?.select) {
        nextRef.current.select();
      }
    }
  };

  // Form inputs change handlers
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Fetch customer registrations from database (find list)
  const fetchCustomersList = async () => {
    try {
      const customerCode = localStorage.getItem("customerCode") || "";
      const response = await axios.get(`${apiBaseUrl}/main/CustomerAppReg/find?dbCode=${dbCode}&customerCode=${customerCode}&search=${searchQuery}&status=${activeTab}`);
      if (response.data && response.data.success) {
        const mapped = response.data.data.map(item => ({
          id: item.Auto,
          schemeName: item.SchemeLabel,
          customerName: item.CustomerLabel,
          accountNo: item.AccountLabel,
          userName: item.UserName,
          password: item.Password,
          email: item.Email || "-",
          mobile: item.Mobile || "-",
          status: item.Status || "Active",
          lastLogin: item.LastLogin ? new Date(item.LastLogin).toLocaleString() : "-"
        }));
        setCustomers(mapped);
      }
    } catch (error) {
      console.error("Error fetching customers list:", error);
    }
  };

  // Trigger registrations list fetch on parameter changes
  useEffect(() => {
    if (dbCode) {
      fetchCustomersList();
    }
  }, [dbCode, searchQuery, activeTab]);

  // Fetch schemes and registered customers lookup data from database
  const loadData = useCallback(async () => {
    try {
      // 1. Fetch schemes
      const schemesResponse = await axios.get(`${apiBaseUrl}/main/getSchemes/${dbCode}`);
      if (schemesResponse.data.success) {
        const formattedSchemes = schemesResponse.data.data.map(item => ({
          label: item.SchemeName,
          value: item.SchemeId
        }));
        setSchemeOptions(formattedSchemes);
      }

      // 2. Fetch Customer names (using SchRec_Cname)
      const customersResponse = await axios.get(`${apiBaseUrl}/main/SchRec_Cname/${dbCode}`);
      // 3. Fetch Account numbers (using SchRec_Accno)
      const accountsResponse = await axios.get(`${apiBaseUrl}/main/SchRec_Accno/${dbCode}`);
      
      if (customersResponse.data && accountsResponse.data) {
        // Filter customer list to only include customers who have at least one registered scheme account
        const registeredLedgerCodes = new Set(accountsResponse.data.map(acc => acc.accname));
        
        const filteredCustomers = customersResponse.data
          .filter(item => registeredLedgerCodes.has(item.Ledcode))
          .map(item => ({
            label: item.LedName,
            value: item.Ledcode
          }));
        setCustomerNameOptions(filteredCustomers);

        const formattedAccounts = accountsResponse.data.map(item => ({
          label: item.accno,
          value: item.auto,
          accname: item.accname,
          schemeId: item.schemeId
        }));
        setAccountNoOptions(formattedAccounts);
      }
    } catch (error) {
      console.error("Error loading scheme/customer options:", error);
    }
  }, [dbCode, apiBaseUrl]);

  // Initial load
  useEffect(() => {
    if (dbCode) {
      loadData();
    }
  }, [dbCode, loadData]);

  // WebSocket real-time updates for Scheme/Ledger registrations
  useEffect(() => {
    const socket = io(apiBaseUrl);

    const handleDataReload = () => {
      loadData();
    };

    socket.on("SchemeUpdated", handleDataReload);
    socket.on("LedgerUpdated", handleDataReload);
    socket.on("entrynoUpdated", handleDataReload);

    return () => {
      socket.disconnect();
    };
  }, [apiBaseUrl, loadData]);



  // Convert table data to CSV/Excel and trigger download
  const handleExportToExcel = () => {
    if (filteredCustomers.length === 0) {
      alert("No data available to export.");
      return;
    }

    const headers = ["Scheme Name", "Customer Name", "Account No", "User Name", "Email ID", "Status", "Last Login"];
    const rows = filteredCustomers.map(cust => [
      cust.schemeName || "",
      cust.customerName || "",
      cust.accountNo || "",
      cust.userName || "",
      cust.email || "",
      cust.status || "",
      cust.lastLogin || ""
    ]);

    const csvContent = "\uFEFF" + [
      headers.join(","),
      ...rows.map(row => 
        row.map(val => {
          const stringVal = String(val).replace(/"/g, '""');
          return stringVal.includes(",") || stringVal.includes("\n") || stringVal.includes('"') 
            ? `"${stringVal}"` 
            : stringVal;
        }).join(",")
      )
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Registered_Customers_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Save / Update customer
  const handleSave = async (e) => {
    e.preventDefault();
    if (isEditing && !hasEditPermission) {
      alert("Permission Denied: You do not have permission to edit customer app access.");
      return;
    }
    if (!isEditing && !hasSavePermission) {
      alert("Permission Denied: You do not have permission to register new customer app access.");
      return;
    }
    if (
      !formData.schemeName?.label || 
      !formData.customerName?.label || 
      !formData.accountNo?.label || 
      !formData.userName?.trim() || 
      !formData.password?.trim() || 
      !formData.email?.trim() || 
      !formData.mobile?.trim()
    ) {
      alert("Please fill all fields. Every field is required.");
      return;
    }

    const payload = {
      Auto: isEditing ? editingId : 0,
      SchemeValue: formData.schemeName?.value || 0,
      SchemeLabel: formData.schemeName?.label || "",
      CustomerValue: formData.customerName?.value || 0,
      CustomerLabel: formData.customerName?.label || "",
      AccountValue: formData.accountNo?.value || "",
      AccountLabel: formData.accountNo?.label || "",
      UserName: formData.userName,
      Password: formData.password,
      Email: formData.email || "",
      Mobile: formData.mobile || "",
      Location: activeLocation?.id || 0,
      Status: formData.status || "Active",
      DbCode: dbCode,
      CustomerCode: localStorage.getItem("customerCode") || ""
    };

    try {
      const response = await axios.post(`${apiBaseUrl}/main/CustomerAppReg/save`, payload);
      if (response.data.success) {
        alert(isEditing ? "Customer updated successfully!" : "Customer registered successfully for mobile app!");
        handleClearForm();
        fetchCustomersList();
      } else {
        alert(response.data.message || "Failed to save customer app access details");
      }
    } catch (error) {
      alert(error.response?.data?.message || error.message || "Error saving customer app access");
    }
  };

  // Edit Customer
  const handleEdit = (cust) => {
    if (!hasEditPermission) {
      alert("Permission Denied: You do not have permission to edit customer credentials.");
      return;
    }
    setIsEditing(true);
    setEditingId(cust.id);
    
    const matchScheme = schemeOptions.find(s => s.label === cust.schemeName);
    const matchCust = customerNameOptions.find(c => c.label === cust.customerName);
    const matchAcc = accountNoOptions.find(a => a.label === cust.accountNo);

    setFormData({
      schemeName: matchScheme ? { value: matchScheme.value, label: matchScheme.label } : { value: null, label: cust.schemeName },
      customerName: matchCust ? { value: matchCust.value, label: matchCust.label } : { value: null, label: cust.customerName },
      accountNo: matchAcc ? { value: matchAcc.value, label: matchAcc.label } : { value: null, label: cust.accountNo },
      userName: cust.userName,
      password: cust.password,
      email: cust.email === "-" ? "" : cust.email,
      mobile: cust.mobile === "-" ? "" : cust.mobile,
      status: cust.status || "Active"
    });
  };

  // Delete Customer
  const handleDelete = async (id) => {
    if (!hasDeletePermission) {
      alert("Permission Denied: You do not have permission to revoke customer app access.");
      return;
    }
    if (window.confirm("Are you sure you want to delete this customer's app access?")) {
      try {
        const response = await axios.delete(`${apiBaseUrl}/main/CustomerAppReg/delete/${id}`);
        if (response.data.success) {
          alert("Access revoked successfully!");
          fetchCustomersList();
        } else {
          alert(response.data.message || "Failed to revoke access");
        }
      } catch (error) {
        alert("Failed to delete record: " + error.message);
      }
    }
  };

  // Toggle customer status
  const handleToggleStatus = async (id) => {
    if (!hasEditPermission) {
      alert("Permission Denied: You do not have permission to change customer account status.");
      return;
    }
    const cust = customers.find(c => c.id === id);
    if (!cust) return;
    
    const matchScheme = schemeOptions.find(s => s.label === cust.schemeName);
    const matchCust = customerNameOptions.find(c => c.label === cust.customerName);
    const matchAcc = accountNoOptions.find(a => a.label === cust.accountNo);

    const payload = {
      Auto: id,
      SchemeValue: matchScheme ? matchScheme.value : 0,
      SchemeLabel: cust.schemeName,
      CustomerValue: matchCust ? matchCust.value : 0,
      CustomerLabel: cust.customerName,
      AccountValue: matchAcc ? matchAcc.value : "",
      AccountLabel: cust.accountNo,
      UserName: cust.userName,
      Password: cust.password,
      Email: cust.email === "-" ? "" : cust.email,
      Mobile: cust.mobile === "-" ? "" : cust.mobile,
      Location: activeLocation?.id || 0,
      Status: cust.status === "Active" ? "Inactive" : "Active",
      DbCode: dbCode,
      CustomerCode: localStorage.getItem("customerCode") || ""
    };

    try {
      await axios.post(`${apiBaseUrl}/main/CustomerAppReg/save`, payload);
      fetchCustomersList();
    } catch (error) {
      alert("Failed to update status: " + error.message);
    }
  };

  // Clear Form
  const handleClearForm = () => {
    setFormData({
      schemeName: { value: "", label: "" },
      customerName: { value: "", label: "" },
      accountNo: { value: "", label: "" },
      userName: "",
      password: "",
      email: "",
      mobile: "",
      status: "Active"
    });
    setIsEditing(false);
    setEditingId(null);
    schemeComboRef.current?.clearInput?.();
    customerComboRef.current?.clearInput?.();
    accountComboRef.current?.clearInput?.();
  };

  // Filtered & Paginated records
  const filteredCustomers = customers.filter(cust => {
    const matchesSearch = 
      cust.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cust.accountNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cust.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cust.mobile.includes(searchQuery);
    
    if (activeTab === "All") return matchesSearch;
    return matchesSearch && cust.status === activeTab;
  });

  const totalPages = Math.ceil(filteredCustomers.length / pageSize) || 1;
  const paginatedCustomers = filteredCustomers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Statistics counters
  const totalCount = customers.length;
  const activeCount = customers.filter(c => c.status === "Active").length;
  const inactiveCount = customers.filter(c => c.status === "Inactive").length;

  return (
    <div
      className="absolute inset-0 w-full h-full bg-[#f0f0f0] flex items-center justify-center pointer-events-auto select-none"
      style={{ zIndex: zIndex || 101 }}
    >
      <div className="bg-white font-normal w-[99%] max-w-[1440px] h-[99%] max-h-[900px] border border-slate-200 shadow-md flex flex-col overflow-hidden absolute">
        
        {/* Header */}
        <WindowHeader
          title="Customer Management"
          logo={headeLogo}
          onClose={onClose}
          className="bg-[#0f2d59] text-white h-9 px-3 shrink-0"
          closeBtnClassName="h-6 w-6 my-auto mr-1 bg-red-600 hover:bg-red-700 rounded-sm border-none flex items-center justify-center text-white"
        />

        {/* Action Toolbar Panel / Mini-Header info stats */}
        <div className="bg-white border-b border-slate-200/80 px-4 py-2 shrink-0 flex flex-nowrap items-center justify-between gap-2.5 overflow-x-auto">
          <div className="flex items-center gap-2.5 shrink-0">
            <div className="w-11 h-11 bg-[#0d2347] rounded-full flex items-center justify-center text-white shrink-0 shadow-sm">
              <FaUserPlus className="w-4.5 h-4.5 text-blue-100" />
            </div>
            <div>
              <h1 className="text-[17px] font-bold text-slate-800 leading-tight">Customer Management</h1>
              <p className="text-[11.5px] text-slate-500 font-medium">Add and manage customers who can access the mobile app</p>
            </div>
          </div>

          {/* Counters row & Action Buttons */}
          <div className="flex items-center gap-2.5 ml-auto shrink-0 flex-nowrap">
            {/* Total Customers */}
            <div className="flex items-center gap-2 bg-slate-50 px-2.5 py-1 rounded-xl border border-slate-200/80 shrink-0">
              <div className="w-7.5 h-7.5 rounded-full bg-blue-100/50 text-[#0f2d59] flex items-center justify-center shrink-0"><FiUser className="w-4 h-4" /></div>
              <div className="leading-tight">
                <span className="text-[9.5px] text-slate-400 font-bold block uppercase tracking-wider mb-0.5">Total Customers</span>
                <span className="text-[14px] font-extrabold text-slate-700">{totalCount}</span>
                <span className="text-[8.5px] text-slate-400 block mt-0.5">All Customers</span>
              </div>
            </div>
            {/* Active Customers */}
            <div className="flex items-center gap-2 bg-slate-50 px-2.5 py-1 rounded-xl border border-slate-200/80 shrink-0">
              <div className="w-7.5 h-7.5 rounded-full bg-emerald-100/50 text-emerald-600 flex items-center justify-center shrink-0"><FiCheckCircle className="w-4 h-4" /></div>
              <div className="leading-tight">
                <span className="text-[9.5px] text-slate-400 font-bold block uppercase tracking-wider mb-0.5">Active Customers</span>
                <span className="text-[14px] font-extrabold text-slate-700">{activeCount}</span>
                <span className="text-[8.5px] text-slate-400 block mt-0.5">Active Accounts</span>
              </div>
            </div>
            {/* Inactive Customers */}
            <div className="flex items-center gap-2 bg-slate-50 px-2.5 py-1 rounded-xl border border-slate-200/80 shrink-0">
              <div className="w-7.5 h-7.5 rounded-full bg-rose-100/50 text-rose-600 flex items-center justify-center shrink-0"><FiTrash2 className="w-4 h-4" /></div>
              <div className="leading-tight">
                <span className="text-[9.5px] text-slate-400 font-bold block uppercase tracking-wider mb-0.5">Inactive Customers</span>
                <span className="text-[14px] font-extrabold text-slate-700">{inactiveCount}</span>
                <span className="text-[8.5px] text-slate-400 block mt-0.5">Inactive Accounts</span>
              </div>
            </div>

            {/* Header buttons */}
            <div className="flex items-center gap-2 ml-2 shrink-0">
              <button
                type="button"
                onClick={handleExportToExcel}
                className="h-8 px-3 border border-[#b78a39] hover:bg-amber-50/50 text-[#b78a39] font-bold rounded-lg text-xs flex items-center gap-1.5 transition-colors cursor-pointer bg-white"
              >
                <FiDownload className="w-3.5 h-3.5" />
                <span>Export to Excel</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  handleClearForm();
                  onOpenSchemeRegistration?.("", formData.schemeName);
                }}
                className="h-8 px-3 bg-[#0f2d59] hover:bg-[#0d2347] text-white font-bold rounded-lg text-xs flex items-center gap-1.5 transition-colors cursor-pointer border-none"
              >
                <FiPlus className="w-4 h-4" />
                <span>Add New Customer</span>
              </button>
            </div>
          </div>
        </div>

        {/* Main Work Area split */}
        <div className="flex-1 flex gap-4 p-4 min-h-0 bg-slate-50 overflow-hidden">
          
          {/* LEFT FORM PANEL (32% width) */}
          <div className="w-[32%] bg-white border border-slate-200/80 rounded-xl shadow-sm flex flex-col overflow-hidden shrink-0 justify-between">
            <div className="flex flex-col gap-2.5 p-3 flex-1 min-h-0">
              
              {/* Form Title banner */}
              <div className="bg-gradient-to-r from-[#051126] via-[#0b2246] to-[#143260] rounded-t-xl py-2 px-3 text-white flex items-center gap-2.5 relative overflow-hidden shadow-xs shrink-0 select-none min-h-[38px]">
                
                {/* Gold outline icon */}
                <div className="relative flex items-center justify-center shrink-0">
                  <FiUser className="w-5 h-5 text-amber-300 stroke-[1.5]" />
                  <span className="absolute right-[-4px] bottom-[-4px] text-amber-300 font-extrabold text-[12px] w-3 h-3 rounded-full flex items-center justify-center">+</span>
                </div>

                <div className="z-10 leading-tight">
                  <h3 className="text-[12px] font-bold uppercase tracking-wider text-[#ffd700]" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}>{isEditing ? "Edit Customer Info" : "Add New Customer"}</h3>
                  <p className="text-[9px] text-slate-300 font-medium leading-none mt-0.5">Fill in customer details to give mobile app access</p>
                </div>

                {/* Golden slash design block on the far right (Precise SVG layout matching mockup) */}
                <div className="absolute right-0 top-0 h-full w-32 pointer-events-none rounded-tr-xl overflow-hidden">
                  <svg 
                    className="absolute right-0 top-0 h-full w-full pointer-events-none" 
                    viewBox="0 0 120 44" 
                    preserveAspectRatio="none"
                  >
                    <defs>
                      {/* Metallic Gold Gradient */}
                      <linearGradient id="goldGrad" x1="0%" y1="100%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#a67c1e" />
                        <stop offset="30%" stopColor="#f1c40f" />
                        <stop offset="50%" stopColor="#fbf3b9" />
                        <stop offset="70%" stopColor="#f1c40f" />
                        <stop offset="100%" stopColor="#aa7c11" />
                      </linearGradient>
                      {/* Honeycomb Pattern */}
                      <pattern id="honeycomb" width="6" height="6" patternUnits="userSpaceOnUse">
                        <path d="M 3 0 L 6 1.5 L 6 4.5 L 3 6 L 0 4.5 L 0 1.5 Z" fill="none" stroke="#aa7c11" strokeWidth="0.4" opacity="0.35" />
                      </pattern>
                    </defs>
                    
                    {/* Bottom-right gold patterned triangle */}
                    {/* Points: bottom-left of triangle (x=78, y=44), bottom-right (x=120, y=44), top-right (x=120, y=14) */}
                    <polygon points="78,44 120,44 120,14" fill="url(#goldGrad)" />
                    <polygon points="78,44 120,44 120,14" fill="url(#honeycomb)" />
                    
                    {/* Inner/Right Gold diagonal line */}
                    <line x1="78" y1="44" x2="120" y2="14" stroke="url(#goldGrad)" strokeWidth="2.2" />
                    
                    {/* Outer/Left Gold diagonal line */}
                    <line x1="66" y1="44" x2="108" y2="14" stroke="url(#goldGrad)" strokeWidth="1.5" />
                  </svg>
                </div>

              </div>

              {/* Form Fields List */}
              <div className="flex flex-col gap-2 overflow-y-auto pr-0.5 flex-1">
                
                 {/* Scheme Name dropdown */}
                <div className="flex flex-col gap-0.5">
                  <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1">
                    <FiBookOpen className="w-3 h-3 text-slate-400" />
                    Scheme Name <span className="text-rose-500">*</span>
                  </label>
                  <SherComboBox
                    comboRef={schemeComboRef}
                    id="customer-app-scheme-combo"
                    options={schemeOptions}
                    value={formData.schemeName?.label || ""}
                    onSelect={(opt) => handleInputChange("schemeName", opt)}
                    onKeyDown={(e) => handleKeyDownNext(e, customerComboRef)}
                    placeholder="Select Scheme"
                    showArrow={true}
                    clearable={false}
                    inputWidth="100%"
                    inputHeight="28px"
                    inputClassName="border border-slate-200 rounded-md hover:border-slate-300 focus:border-[#0f2d59]"
                    inputStyle={{ fontSize: "11.5px" }}
                  />
                </div>

                {/* Customer Name dropdown/search + create new inline */}
                <div className="flex flex-col gap-0.5">
                  <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1">
                    <FiUser className="w-3 h-3 text-slate-400" />
                    Customer Name <span className="text-rose-500">*</span>
                  </label>
                  <div className="flex items-center gap-1.5 w-full">
                    <div className="flex-1">
                      <SherComboBox
                        comboRef={customerComboRef}
                        options={customerNameOptions}
                        value={formData.customerName?.label || ""}
                        onSelect={(opt) => {
                          handleInputChange("customerName", opt);
                          const matchedAcc = accountNoOptions.find(acc => acc.accname === opt.value);
                          if (matchedAcc) {
                            handleInputChange("accountNo", { value: matchedAcc.value, label: matchedAcc.label });
                            if (matchedAcc.schemeId && schemeOptions.length > 0) {
                              const matchedScheme = schemeOptions.find(s => s.value === matchedAcc.schemeId);
                              if (matchedScheme) {
                                handleInputChange("schemeName", { value: matchedScheme.value, label: matchedScheme.label });
                              }
                            }
                          }
                        }}
                        onKeyDown={(e) => handleKeyDownNext(e, accountComboRef)}
                        placeholder="Select or type customer name"
                        showArrow={true}
                        clearable={false}
                        inputWidth="100%"
                        inputHeight="28px"
                        inputClassName="border border-slate-200 rounded-md hover:border-slate-300 focus:border-[#0f2d59]"
                        inputStyle={{ fontSize: "11.5px" }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const typedName = customerComboRef.current?.value || "";
                        onOpenSchemeRegistration?.(typedName.trim(), formData.schemeName);
                      }}
                      className="w-8 h-[28px] bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-md flex items-center justify-center border border-slate-200 transition-colors shrink-0 cursor-pointer"
                      title="Register Customer Scheme"
                    >
                      <FiPlus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Account Number */}
                <div className="flex flex-col gap-0.5">
                  <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1">
                    <FiCreditCard className="w-3 h-3 text-slate-400" />
                    Account Number <span className="text-rose-500">*</span>
                  </label>
                  <SherComboBox
                    comboRef={accountComboRef}
                    options={accountNoOptions}
                    value={formData.accountNo?.label || ""}
                    onSelect={(opt) => {
                      handleInputChange("accountNo", opt);
                      if (opt.accname && customerNameOptions.length > 0) {
                        const matchedCust = customerNameOptions.find(c => c.value === opt.accname);
                        if (matchedCust) {
                          handleInputChange("customerName", { value: matchedCust.value, label: matchedCust.label });
                        }
                      }
                      if (opt.schemeId && schemeOptions.length > 0) {
                        const matchedScheme = schemeOptions.find(s => s.value === opt.schemeId);
                        if (matchedScheme) {
                          handleInputChange("schemeName", { value: matchedScheme.value, label: matchedScheme.label });
                        }
                      }
                    }}
                    onFreeInput={(txt) => handleInputChange("accountNo", { value: null, label: txt })}
                    onKeyDown={(e) => handleKeyDownNext(e, userNameRef)}
                    placeholder="Select account number"
                    showArrow={true}
                    clearable={false}
                    inputWidth="100%"
                    inputHeight="28px"
                    inputClassName="border border-slate-200 rounded-md hover:border-slate-300 focus:border-[#0f2d59]"
                    inputStyle={{ fontSize: "11.5px" }}
                  />
                </div>

                 {/* User Name */}
                <div className="flex flex-col gap-0.5">
                  <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1">
                    <FiUser className="w-3 h-3 text-slate-400" />
                    User Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    ref={userNameRef}
                    value={formData.userName}
                    onChange={(e) => handleInputChange("userName", e.target.value)}
                    onKeyDown={(e) => handleKeyDownNext(e, passwordRef)}
                    placeholder="Enter user name"
                    className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all"
                  />
                  <span className="text-[9px] text-slate-400 font-medium">Use a unique username for app login</span>
                </div>

                {/* Password */}
                <div className="flex flex-col gap-0.5">
                  <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1">
                    <FiLock className="w-3 h-3 text-slate-400" />
                    Password <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative flex items-center w-full">
                    <input
                      type={showPassword ? "text" : "password"}
                      ref={passwordRef}
                      value={formData.password}
                      onChange={(e) => handleInputChange("password", e.target.value)}
                      onKeyDown={(e) => handleKeyDownNext(e, emailRef)}
                      placeholder="Enter password"
                      className="w-full h-[28px] pl-2.5 pr-8 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2.5 text-slate-400 hover:text-slate-600 flex items-center justify-center border-none bg-transparent cursor-pointer"
                    >
                      {showPassword ? <FiEyeOff className="w-3.5 h-3.5" /> : <FiEye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  <span className="text-[9px] text-slate-400 font-medium">Minimum 6 characters</span>
                </div>

                {/* Email ID */}
                <div className="flex flex-col gap-0.5">
                  <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1">
                    <FiMail className="w-3 h-3 text-slate-400" />
                    Email ID <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="email"
                    ref={emailRef}
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    onKeyDown={(e) => handleKeyDownNext(e, mobileRef)}
                    placeholder="Enter email id"
                    className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all"
                  />
                  <span className="text-[9px] text-slate-400 font-medium">For password recovery and notifications</span>
                </div>

                {/* Mobile Number */}
                <div className="flex flex-col gap-0.5">
                  <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1">
                    <FiPhone className="w-3 h-3 text-slate-400" />
                    Mobile Number <span className="text-rose-500">*</span>
                  </label>
                  <div className="flex items-center gap-2 w-full">
                    <div className="w-16">
                      <select className="w-full h-[28px] bg-slate-50 border border-slate-200 rounded-md text-slate-700 text-[11.5px] px-1 font-semibold outline-none hover:border-slate-300">
                        <option>+91</option>
                        <option>+971</option>
                        <option>+1</option>
                      </select>
                    </div>
                    <input
                      type="text"
                      ref={mobileRef}
                      maxLength={10}
                      value={formData.mobile}
                      onChange={(e) => handleInputChange("mobile", e.target.value)}
                      onKeyDown={(e) => handleKeyDownNext(e, statusToggleRef)}
                      placeholder="Enter mobile number"
                      className="flex-1 h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all"
                    />
                  </div>
                </div>

                {/* Status Toggle Switch */}
                <div className="flex items-center justify-between bg-slate-50 p-2 rounded-lg border border-slate-200/60 mt-1 select-none">
                  <div className="flex flex-col">
                    <span className="text-[11px] font-bold text-slate-700">Account Access Status</span>
                    <span className="text-[9px] text-slate-400">Toggle mobile app access permission</span>
                  </div>
                  <button
                    type="button"
                    ref={statusToggleRef}
                    onKeyDown={(e) => handleKeyDownNext(e, saveButtonRef)}
                    onClick={() => handleInputChange("status", formData.status === "Active" ? "Inactive" : "Active")}
                    className={`w-10 h-[22px] flex items-center rounded-full p-0.5 cursor-pointer transition-colors duration-300 outline-none border-none ${formData.status === "Active" ? "bg-[#b78a39]" : "bg-slate-300"}`}
                  >
                    <div
                      className={`bg-white w-[18px] h-[18px] rounded-full shadow-md transform transition-transform duration-300 ${formData.status === "Active" ? "translate-x-[18px]" : "translate-x-0"}`}
                    />
                  </button>
                </div>

              </div>

              {/* Form Buttons */}
              <div className="grid grid-cols-2 gap-2.5 pt-2 border-t border-slate-100 shrink-0">
                <button
                  type="button"
                  onClick={handleClearForm}
                  className="h-8 border border-slate-300 hover:bg-slate-50 text-slate-600 font-bold rounded-md transition-colors cursor-pointer text-xs"
                >
                  Clear Form
                </button>
                <button
                  type="button"
                  ref={saveButtonRef}
                  onClick={handleSave}
                  disabled={isEditing ? !hasEditPermission : !hasSavePermission}
                  className={`h-8 font-bold rounded-md transition-colors shadow-sm flex items-center justify-center gap-1.5 text-xs ${
                    (isEditing ? !hasEditPermission : !hasSavePermission)
                      ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                      : "bg-[#b78a39] hover:bg-[#a67c32] text-white cursor-pointer"
                  }`}
                  title={
                    (isEditing ? !hasEditPermission : !hasSavePermission)
                      ? "Permission Denied"
                      : (isEditing ? "Update Info" : "Save Customer")
                  }
                >
                  <FiCheckCircle className="w-3.5 h-3.5" />
                  <span>{isEditing ? "Update Info" : "Save Customer"}</span>
                </button>
              </div>

            </div>

            {/* Information box footer */}
            <div className="m-2 p-2 bg-amber-50/70 border border-amber-200/60 rounded-md flex items-center gap-2 text-amber-800 shrink-0">
              <FiInfo className="w-3.5 h-3.5 text-amber-600 shrink-0" />
              <p className="text-[10px] font-medium leading-tight">
                Customer will use these credentials to login to the mobile app.
              </p>
            </div>

          </div>

          {/* RIGHT GRID PANEL (68% width) */}
          <div className="flex-1 bg-white border border-slate-200/80 rounded-xl shadow-sm flex flex-col overflow-hidden">
            
            {/* Grid Header Search & Filters */}
            <div className="p-3.5 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3 bg-white shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center shadow-xs"><FaListAlt className="w-4 h-4" /></div>
                <div>
                  <h3 className="text-sm font-bold text-slate-800 leading-none">Registered Customers</h3>
                  <span className="text-[11.5px] text-slate-400 font-medium mt-1 block">Manage customers who can access the mobile app</span>
                </div>
              </div>

              {/* Filters Box */}
              <div className="flex items-center gap-2">
                
                {/* Search box */}
                <div className="relative flex items-center">
                  <input
                    type="text"
                    value={searchQuery}
                    disabled={!hasFindPermission}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={!hasFindPermission ? "Find/Search permission disabled..." : "Search by name, account no, mobile..."}
                    className={`pl-3 pr-8 w-64 h-8 border rounded-lg text-xs outline-none transition-all font-medium ${
                      !hasFindPermission
                        ? "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"
                        : "bg-[#f8fafc] border-slate-200 text-slate-800 hover:border-slate-300 focus:border-[#0f2d59] focus:bg-white"
                    }`}
                  />
                  <FiSearch className="absolute right-3 text-slate-400 w-3.5 h-3.5 pointer-events-none" />
                </div>

                {/* Filter list button */}
                <button className="h-8 px-3.5 border border-slate-200 hover:bg-slate-50 text-slate-600 font-bold rounded-lg text-xs flex items-center gap-1.5 transition-colors cursor-pointer bg-white">
                  <FiFilter className="w-3.5 h-3.5 text-slate-500" />
                  <span>Filter</span>
                </button>

                {/* More options dots button */}
                <button className="h-8 w-8 border border-slate-200 hover:bg-slate-50 text-slate-500 rounded-lg flex items-center justify-center transition-colors cursor-pointer bg-white">
                  <FiMoreVertical className="w-4 h-4" />
                </button>

              </div>
            </div>

            {/* Tabs row & stats count */}
            <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
              <div className="flex gap-1">
                {["All", "Active", "Inactive"].map(tab => {
                  const count = tab === "All" ? totalCount : tab === "Active" ? activeCount : inactiveCount;
                  const isActive = activeTab === tab;
                  return (
                    <button
                      key={tab}
                      onClick={() => {
                        setActiveTab(tab);
                        setCurrentPage(1);
                      }}
                      className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 flex items-center gap-1.5 cursor-pointer border ${
                        isActive
                          ? "bg-[#0f2d59] text-white border-[#0f2d59]"
                          : "text-slate-500 hover:text-slate-800 border-transparent hover:bg-slate-100"
                      }`}
                    >
                      <span>{tab}</span>
                      <span className={`px-1.5 py-0.2 text-[10px] rounded-full font-bold ${isActive ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Customers Data Table */}
            <div className="flex-1 overflow-auto bg-white relative">
              <table className="w-full text-left border-collapse text-xs">
                <thead className="bg-[#0f2d59] text-white sticky top-0 font-semibold z-10">
                  <tr className="h-9">
                    <th className="py-2 px-3 text-center border-r border-[#0f2d59]/20 w-12">#</th>
                    <th className="py-2 px-3 border-r border-[#0f2d59]/20">Scheme Name</th>
                    <th className="py-2 px-3 border-r border-[#0f2d59]/20">Customer Name</th>
                    <th className="py-2 px-3 border-r border-[#0f2d59]/20">Account No</th>
                    <th className="py-2 px-3 border-r border-[#0f2d59]/20">User Name</th>
                    <th className="py-2 px-3 border-r border-[#0f2d59]/20">Email ID</th>
                    <th className="py-2 px-3 text-center border-r border-[#0f2d59]/20">Status</th>
                    <th className="py-2 px-3 border-r border-[#0f2d59]/20">Last Login</th>
                    <th className="py-2 px-3 text-center w-28">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                  {paginatedCustomers.map((cust, idx) => {
                    const rowNum = (currentPage - 1) * pageSize + idx + 1;
                    const rowBg = idx % 2 === 0 ? "bg-white" : "bg-slate-50/50";
                    const isGoldScheme = cust.schemeName === "Gold Savings";

                    return (
                      <tr key={cust.id} className={`${rowBg} hover:bg-blue-50/30 transition-colors`}>
                        
                        {/* Row number */}
                        <td className="p-2.5 text-center text-slate-500 font-bold border-r border-slate-100">{rowNum}</td>
                        
                        {/* Scheme name with gold badge if Applicable */}
                        <td className="p-2.5 border-r border-slate-100">
                          <div className="flex items-center gap-1.5">
                            {isGoldScheme ? (
                              <span className="w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold text-[9px]">★</span>
                            ) : cust.schemeName === "Daily Collection" ? (
                              <span className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-[9px]">◆</span>
                            ) : (
                              <span className="w-5 h-5 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold text-[9px]">●</span>
                            )}
                            <span className="font-semibold text-slate-800">{cust.schemeName}</span>
                          </div>
                        </td>
                        
                        {/* Customer Name */}
                        <td className="p-2.5 border-r border-slate-100 text-slate-900 font-bold">{cust.customerName}</td>
                        
                        {/* Account No */}
                        <td className="p-2.5 border-r border-slate-100 font-mono text-xs text-slate-600 font-semibold">{cust.accountNo}</td>
                        
                        {/* Username */}
                        <td className="p-2.5 border-r border-slate-100 text-slate-600">{cust.userName}</td>
                        
                        {/* Email */}
                        <td className="p-2.5 border-r border-slate-100 text-slate-500">{cust.email}</td>
                        
                        {/* Status Toggle Badge */}
                        <td className="p-2.5 text-center border-r border-slate-100">
                          <button
                            type="button"
                            disabled={!hasEditPermission}
                            onClick={() => handleToggleStatus(cust.id)}
                            className={`px-2.5 py-0.5 rounded-full text-[10.5px] font-bold border select-none transition-all ${
                              !hasEditPermission
                                ? "opacity-50 cursor-not-allowed bg-slate-100 text-slate-400 border-slate-200"
                                : cust.status === "Active"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:scale-105 cursor-pointer"
                                : "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100 hover:scale-105 cursor-pointer"
                            }`}
                            title={!hasEditPermission ? "Permission Denied: Edit permission required to change status" : "Click to toggle status"}
                          >
                            {cust.status}
                          </button>
                        </td>
                        
                        {/* Last Login */}
                        <td className="p-2.5 border-r border-slate-100 text-[11px] text-slate-500">{cust.lastLogin}</td>
                        
                        {/* Actions */}
                        <td className="p-2.5">
                          <div className="flex items-center justify-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => alert(`Showing details for ${cust.customerName}\nAccount: ${cust.accountNo}\nMobile: ${cust.mobile}`)}
                              className="w-7 h-7 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg flex items-center justify-center transition-all cursor-pointer border border-blue-100"
                              title="View details"
                            >
                              <FiEye className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              disabled={!hasEditPermission}
                              onClick={() => handleEdit(cust)}
                              className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all border ${
                                !hasEditPermission
                                  ? "bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed"
                                  : "bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white border-indigo-100 cursor-pointer"
                              }`}
                              title={!hasEditPermission ? "Permission Denied: Edit action disabled" : "Edit credentials"}
                            >
                              <FiEdit3 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              disabled={!hasDeletePermission}
                              onClick={() => handleDelete(cust.id)}
                              className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all border ${
                                !hasDeletePermission
                                  ? "bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed"
                                  : "bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white border-rose-100 cursor-pointer"
                              }`}
                              title={!hasDeletePermission ? "Permission Denied: Delete action disabled" : "Revoke access"}
                            >
                              <FiTrash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>

                      </tr>
                    );
                  })}
                  
                  {paginatedCustomers.length === 0 && (
                    <tr>
                      <td colSpan="9" className="p-6 text-center text-slate-400 font-bold text-xs">
                        No registered customers found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination controls & Entries info */}
            <div className="p-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/40 text-xs shrink-0 select-none">
              <span className="text-slate-500 font-medium">
                Showing {filteredCustomers.length === 0 ? 0 : (currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filteredCustomers.length)} of {filteredCustomers.length} entries
              </span>

              {/* Navigation pagination buttons */}
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                  className="w-8 h-8 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                >
                  <FiChevronsLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  className="w-8 h-8 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                >
                  <FiChevronLeft className="w-4 h-4" />
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                  .map((page, idx, arr) => {
                    const showEllipsis = idx > 0 && page - arr[idx - 1] > 1;
                    return (
                      <React.Fragment key={page}>
                        {showEllipsis && <span className="text-slate-400 px-1 font-bold">...</span>}
                        <button
                          type="button"
                          onClick={() => setCurrentPage(page)}
                          className={`w-8 h-8 rounded-lg font-bold transition-all text-xs cursor-pointer ${
                            currentPage === page
                              ? "bg-[#0f2d59] text-white"
                              : "bg-white border border-slate-200 hover:bg-slate-50 text-slate-600"
                          }`}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  })
                }

                <button
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  className="w-8 h-8 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                >
                  <FiChevronRight className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                  className="w-8 h-8 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                >
                  <FiChevronsRight className="w-4 h-4" />
                </button>

                {/* Page Size select */}
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="ml-2 h-8 px-2 bg-white border border-slate-200 rounded-lg text-slate-600 font-semibold outline-none hover:border-slate-300"
                >
                  {[5, 10, 20, 50].map(size => (
                    <option key={size} value={size}>{size} / page</option>
                  ))}
                </select>
              </div>

            </div>

          </div>

        </div>

        {/* Universal Stylesheet injection to guarantee Inter font on all elements */}
        <style dangerouslySetInnerHTML={{__html: `
          * { font-family: 'Inter', sans-serif !important; }
          
          /* Custom scrollbars for grids and overflow components */
          .scrollbar-thin::-webkit-scrollbar {
            width: 4px;
            height: 4px;
          }
          .scrollbar-thin::-webkit-scrollbar-track {
            background: transparent;
          }
          .scrollbar-thin::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 4px;
          }
          .scrollbar-thin::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}} />

      </div>
    </div>
  );
}

export default CustomerAppReg;
